/**
 * 데미지 계산. Phaser 에 의존하지 않는 순수 함수로 유지한다
 * (추후 멀티플레이 서버에서 그대로 재사용하기 위해).
 * 공식은 docs/game-design.md 4장 참고.
 */

import { COMBAT } from '../data/balance';

export interface AttackerStats {
  attackMin: number;
  attackMax: number;
  critRate: number;
  critMultiplier: number;
}

export interface DamageResult {
  amount: number;
  crit: boolean;
}

export type Rng = () => number;

function between(min: number, max: number, rng: Rng): number {
  return min + (max - min) * rng();
}

export function rollPlayerDamage(
  attacker: AttackerStats,
  targetDefense: number,
  multiplier: number,
  rng: Rng = Math.random,
): DamageResult {
  const base = between(attacker.attackMin, attacker.attackMax, rng) * multiplier;
  const afterDefense = base - targetDefense * COMBAT.defenseFactor;
  const varied = afterDefense * (1 + (rng() * 2 - 1) * COMBAT.variance);
  const crit = rng() < attacker.critRate;
  const total = crit ? varied * attacker.critMultiplier : varied;
  return {
    amount: Math.max(COMBAT.minDamage, Math.floor(total)),
    crit,
  };
}

export function rollMonsterDamage(
  monsterAttack: number,
  playerDefense: number,
  rng: Rng = Math.random,
): number {
  const base = monsterAttack - playerDefense * COMBAT.defenseFactor;
  const varied = base * (1 + (rng() * 2 - 1) * COMBAT.variance);
  return Math.max(COMBAT.minDamage, Math.floor(varied));
}

/** 명중률 0~1. 회피 0 이하면 항상 명중. */
export function hitChance(accuracy: number, avoid: number): number {
  if (avoid <= 0) return COMBAT.maxHitRate;
  const raw = (accuracy + COMBAT.hitOffset) / (avoid * COMBAT.avoidWeight + COMBAT.hitOffset);
  return Math.min(COMBAT.maxHitRate, Math.max(COMBAT.minHitRate, raw));
}

export function rollHit(accuracy: number, avoid: number, rng: Rng = Math.random): boolean {
  return rng() < hitChance(accuracy, avoid);
}
